import { useMemo, useState } from 'react';
import LightLogo from '@/assets/svg/Logo';
import DarkLogo from '@/components/icons/Logo';
import { PrimaryButton } from '@/components/buttons/PrimaryButton';
// import LanguageSelectButton from '@/components/header/LanguageSelectButton';
import SocialLinks from '@/components/header/SocialLinks';
import { cn } from '@/lib/utils';
import { Link, NavLink, useLocation, useSearchParams } from 'react-router-dom';
import BurgerIcon from '@/components/icons/BurgerIcon';
import ArrowDownIcon from '@/components/icons/ArrowDownIcon';
import { useTranslation } from 'react-i18next';
import { useLocalizedPath } from '@/lib/i18n/useLocale';
import LanguageSelectButton from '@/components/header/LanguageSelectButton';
import { ModeToggle } from '@/components/header/mode-toggle';
import { useTheme } from '@/components/theme-provider';

const navPaths = [
    { key: 'home', to: '/' },
    { key: 'services', to: '/services' },
    { key: 'products', to: '/products' },
    { key: 'clients', to: '/clients' },
    { key: 'pricing', to: '/pricing' },
    { key: 'about', to: '/us' },
    { key: 'contact', to: '/contact' },
];

const Header = () => {
    const { t } = useTranslation();
    const { theme } = useTheme();
    const localizedPath = useLocalizedPath();
    const location = useLocation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isPricingMenuOpen, setIsPricingMenuOpen] = useState(false);
    const [isMobilePricingMenuOpen, setIsMobilePricingMenuOpen] = useState(false);
    const [searchParams] = useSearchParams();
    const social = searchParams.get('social');
    const isProductsActive = location.pathname.includes('/products');
    const isPricingActive = location.pathname.includes('/pricing');
    const isDark =
        theme === 'dark' ||
        (theme === 'system' &&
            typeof window !== 'undefined' &&
            window.matchMedia('(prefers-color-scheme: dark)').matches);
    const BrandLogo = isDark ? DarkLogo : LightLogo;
    // const isContactPage = location.pathname.endsWith('/contact');

    const pricingSubmenuItems = useMemo(
        () => [
            { key: 'productsCrm', label: 'CRM', to: '/pricing/crm' },
            { key: 'productsHrm', label: 'HRM', to: '/pricing/hrm' },
            { key: 'Clinico', label: 'Clinico', to: '/pricing/clinico' },
        ],
        [],
    );

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
        if (isMenuOpen) {
            setIsMobilePricingMenuOpen(false);
        }
    };

    const navLinkClass = (isActive: boolean, extraActive = false) =>
        cn(
            'tracking-normal text-nowrap leading-[100%] font-medium inline-block pb-1 border-b-2 transition-colors',
            isActive || extraActive
                ? 'text-primery-blue-600 border-primery-blue-600 text-[12px] xl:text-[18px]'
                : 'text-nav border-transparent text-[12px] xl:text-[16px] hover:text-primery-blue-600',
        );

    const authButtons = (mobile = false) => (
        <div className={cn('flex items-center gap-2', mobile && 'w-full flex-col sm:flex-row justify-center')}>
            <Link
                to={localizedPath('/sign-up', { search: social ? `social=${social}` : undefined })}
                onClick={mobile ? toggleMenu : undefined}
                className={mobile ? 'w-full sm:w-auto' : undefined}
            >
                <PrimaryButton
                    className={cn(
                        'bg-primery-blue-800 hover:bg-primery-blue-700 tracking-normal font-cairo font-bold rounded-lg leading-[125%] text-sm md:text-base text-login',
                        mobile ? 'w-full px-3 py-2' : 'px-3 py-2 md:px-4 lg:px-5 md:py-2',
                    )}
                >
                    {t('header.registerButton')}
                </PrimaryButton>
            </Link>

            <Link
                to={localizedPath('/login', { search: social ? `social=${social}` : undefined })}
                onClick={mobile ? toggleMenu : undefined}
                className={mobile ? 'w-full sm:w-auto' : undefined}
            >
                <span
                    className={cn(
                        'inline-flex items-center justify-center border border-border bg-background text-primery-blue-800 hover:bg-accent tracking-normal font-cairo font-bold rounded-lg leading-[125%] text-sm md:text-base transition-colors',
                        mobile ? 'w-full px-3 py-2' : 'px-3 py-2 md:px-4 lg:px-5 md:py-2',
                    )}
                >
                    {t('header.loginButton')}
                </span>
            </Link>

            <div className="flex items-center gap-2 md:gap-4 md:mt-0 mt-4 z-10">
                <ModeToggle />
                <LanguageSelectButton />
            </div>
        </div>
    );

    return (
        <header className="mainContainer w-full">
            {/* Top Bar */}
            {/* <div
                className={cn(
                    'sm:py-3 py-0 pb-2 z-40 rounded-t-[30px] md:rounded-t-[130px] lg:rounded-t-[200px] w-full max-w-[min(100%,95vw)] md:max-w-none mx-auto px-3 sm:px-[14px] md:px-6 lg:px-8',
                    isContactPage ? 'bg-transparent' : 'bg-primery-blue-300',
                )}
            >
                <div className="flex items-center justify-between h-full">
                    <div className="flex items-center gap-2 md:gap-4 md:mt-0 mt-4 z-10">
                        <LanguageSelectButton />
                    </div>
                    <div className="hidden sm:block">
                        <SocialLinks />
                    </div>
                </div>
            </div> */}

            {/* Navigation Bar */}
            <div className={cn('relative flex items-center justify-center w-full', isPricingMenuOpen && 'z-50')}>
                <div className="z-40 absolute top-4 lg:top-[20px] py-3 custom:py-4 px-4 md:px-6 xl:px-10 custom:px-6 bg-background rounded-2xl shadow-md flex flex-col lg:flex-row items-center justify-between xl:gap-1 custom:gap-3 w-full transition-colors">
                    {/* Mobile Menu Button */}
                    <div className="flex items-center justify-between w-full lg:hidden">
                        <button onClick={toggleMenu} className="text-primery-blue-800 focus:outline-none">
                            {isMenuOpen ? (
                                <span className="text-2xl">✕</span>
                            ) : (
                                <span className="text-2xl">
                                    <BurgerIcon />
                                </span>
                            )}
                        </button>
                        <Link to={localizedPath('/', { search: social ? `social=${social}` : undefined })} className="">
                            <BrandLogo className="h-[30px]" />
                        </Link>
                    </div>

                    {/* Desktop Navigation */}
                    <div className="hidden lg:flex items-center justify-between flex-1">
                        {/* Desktop Logo */}
                        <Link
                            to={localizedPath('/', { search: social ? `social=${social}` : undefined })}
                            className="hidden lg:block "
                        >
                            <BrandLogo />
                        </Link>

                        <nav>
                            <ul className="flex gap-1 lg:gap-4">
                                {navPaths.map(({ key, to }) => (
                                    <li key={to} className="flex items-center py-1 px-2 custom:px-[16px]">
                                        {to === '/pricing' ? (
                                            <div
                                                className="relative"
                                                onMouseEnter={() => setIsPricingMenuOpen(true)}
                                                onMouseLeave={() => setIsPricingMenuOpen(false)}
                                            >
                                                <span
                                                    className={cn(
                                                        'inline-flex items-center gap-1.5 tracking-normal text-nowrap leading-[100%] font-medium cursor-default pb-1 border-b-2 transition-colors',
                                                        isPricingActive
                                                            ? 'text-primery-blue-600 border-primery-blue-600 text-[12px]'
                                                            : 'text-nav border-transparent text-[12px] xl:text-[18px]',
                                                    )}
                                                >
                                                    {t(`nav.${key}`)}
                                                    <ArrowDownIcon
                                                        width="14"
                                                        height="14"
                                                        className={cn(
                                                            'inline-flex shrink-0 opacity-80 transition-transform duration-200',
                                                            isPricingMenuOpen && '[&_svg]:-rotate-180',
                                                        )}
                                                    />
                                                </span>
                                                <div
                                                    className={cn(
                                                        'absolute -start-3 top-full pt-4 w-[200px]',
                                                        'z-[9999] transition-all duration-200 ease-out origin-top-left',
                                                        isPricingMenuOpen
                                                            ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto'
                                                            : 'opacity-0 scale-95 -translate-y-1 pointer-events-none',
                                                    )}
                                                >
                                                    <div
                                                        className={cn(
                                                            'rounded-2xl bg-background overflow-hidden',
                                                            'shadow-[0px_18px_40px_-12px_rgba(17,24,39,0.3)] border border-border',
                                                        )}
                                                    >
                                                        {pricingSubmenuItems.map((item) => (
                                                            <NavLink
                                                                key={item.key}
                                                                to={localizedPath(item.to, {
                                                                    search: social ? `social=${social}` : undefined,
                                                                })}
                                                                className="block py-5 px-8 text-[21px] leading-none font-medium text-foreground hover:text-primery-blue-600 hover:bg-accent transition-colors border-b border-border last:border-b-0"
                                                                onClick={() => setIsPricingMenuOpen(false)}
                                                            >
                                                                {t(`nav.${item.key}`, { defaultValue: item.label })}
                                                            </NavLink>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <NavLink
                                                to={localizedPath(to, {
                                                    search: social ? `social=${social}` : undefined,
                                                })}
                                                end={to === '/'}
                                                className={({ isActive }) =>
                                                    navLinkClass(isActive, to === '/products' && isProductsActive)
                                                }
                                            >
                                                {t(`nav.${key}`)}
                                            </NavLink>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </nav>

                        {authButtons()}
                    </div>

                    {/* Mobile Navigation */}
                    {isMenuOpen && (
                        <div className="w-full lg:hidden flex flex-col items-center gap-4 py-4">
                            <nav className="w-full">
                                <ul className="flex flex-col items-center gap-3">
                                    {navPaths.map(({ key, to }) => (
                                        <li key={to} className="w-full text-center">
                                            {to === '/pricing' ? (
                                                <div className="w-full">
                                                    <button
                                                        type="button"
                                                        className={cn(
                                                            'block w-full py-2 px-4 tracking-normal text-nowrap leading-[100%] font-medium',
                                                            isPricingActive
                                                                ? 'text-primery-blue-600 text-lg'
                                                                : 'text-nav text-base',
                                                        )}
                                                        onClick={() => setIsMobilePricingMenuOpen((prev) => !prev)}
                                                    >
                                                        {t(`nav.${key}`)}
                                                    </button>
                                                    {isMobilePricingMenuOpen && (
                                                        <div className="mx-4 mt-2 overflow-hidden rounded-xl border border-border bg-background">
                                                            {pricingSubmenuItems.map((item) => (
                                                                <NavLink
                                                                    key={item.key}
                                                                    to={localizedPath(item.to, {
                                                                        search: social ? `social=${social}` : undefined,
                                                                    })}
                                                                    onClick={toggleMenu}
                                                                    className="block py-3 px-4 text-foreground text-base border-b border-border last:border-b-0"
                                                                >
                                                                    {t(`nav.${item.key}`, { defaultValue: item.label })}
                                                                </NavLink>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <NavLink
                                                    to={localizedPath(to, {
                                                        search: social ? `social=${social}` : undefined,
                                                    })}
                                                    end={to === '/'}
                                                    onClick={toggleMenu}
                                                    className={({ isActive }) =>
                                                        navLinkClass(isActive, to === '/products' && isProductsActive)
                                                    }
                                                >
                                                    {t(`nav.${key}`)}
                                                </NavLink>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </nav>

                            <div className="w-full flex justify-center mt-2">{authButtons(true)}</div>
                            <div className="flex justify-center w-full mt-4">
                                <SocialLinks />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};

export default Header;
